import React, { useEffect, useState, useCallback } from 'react';
import {
    Card,
    Row,
    Col,
    Table,
    Select,
    Modal,
    message,
    Tag,
    Spin,
    Empty,
    Statistic,
    Space,
    Button,
    Input,
    Tooltip,
    Badge,
    Avatar,
} from 'antd';
import {
    UserOutlined,
    CrownOutlined,
    SafetyOutlined,
    EyeOutlined,
    TeamOutlined,
    DashboardOutlined,
    ReloadOutlined,
    SearchOutlined,
    FilterOutlined,
    WarningOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Search } = Input;

const RoleAssignment = () => {
    const { t } = useTranslation();

    const [allSuppliers, setAllSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterRole, setFilterRole] = useState('all');

    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    // ✅ Mevcut kullanıcıyı al
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        setCurrentUser(user);
    }, []);

    // ✅ Fetch suppliers
    const fetchSuppliers = useCallback(async () => {
        try {
            setLoading(true);
            console.log('🔄 Fetching suppliers...');

            const response = await axios.get(`${apiUrl}/api/Suppliers`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const suppliersData = response.data.$values || response.data;
            console.log('✅ Suppliers fetched:', suppliersData.length);

            const formattedSuppliers = suppliersData.map((supplier, index) => ({
                key: index,
                supplierId: supplier.supplierId,
                name: supplier.supplierName,
                email: supplier.businessEmail,
                code: supplier.supplierCode,
                category: supplier.category,
                location: supplier.location,
                role: supplier.role || 'Supplier',
            }));

            setAllSuppliers(formattedSuppliers);
            setFilteredSuppliers(formattedSuppliers);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error(t('data_fetch_error'));
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token, t]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    // ✅ Apply filters
    const applyFilters = useCallback(() => {
        let filtered = [...allSuppliers];

        // Search filter
        if (searchText.trim() !== '') {
            filtered = filtered.filter((supplier) =>
                supplier.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                supplier.email?.toLowerCase().includes(searchText.toLowerCase()) ||
                supplier.code?.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // Role filter
        if (filterRole !== 'all') {
            filtered = filtered.filter((supplier) => supplier.role === filterRole);
        }

        setFilteredSuppliers(filtered);
    }, [allSuppliers, searchText, filterRole]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // ✅ Handle role change
    const handleRoleChange = async (supplierId, newRole, supplierName) => {
        Modal.confirm({
            title: t('role_change_confirmation_title'),
            content: (
                <div>
                    <p>{t('role_change_confirmation_content')}</p>
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f0f2f5', borderRadius: '8px' }}>
                        <strong>{supplierName}</strong>
                        <div style={{ marginTop: '8px' }}>
                            {t('new_role')}: <Tag color={getRoleColor(newRole)}>{newRole}</Tag>
                        </div>
                    </div>
                </div>
            ),
            okText: t('yes'),
            cancelText: t('no'),
            okType: 'primary',
            onOk: async () => {
                try {
                    setLoading(true);
                    await axios.patch(
                        `${apiUrl}/api/Suppliers/${supplierId}/role`,
                        { role: newRole },
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        }
                    );
                    message.success(t('role_updated_successfully'));

                    // Tabloyu güncelle
                    setAllSuppliers(prev =>
                        prev.map(supplier =>
                            supplier.supplierId === supplierId ? { ...supplier, role: newRole } : supplier
                        )
                    );
                } catch (error) {
                    console.error('Error updating role:', error);
                    message.error(t('role_update_error'));
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    // ✅ Role permissions
    const canChangeRole = currentUser?.role === "SuperAdmin";

    // ✅ Role colors
    const getRoleColor = (role) => {
        switch (role) {
            case 'SuperAdmin': return 'red';
            case 'Admin': return 'blue';
            case 'Viewer': return 'green';
            case 'Supplier': return 'orange';
            default: return 'default';
        }
    };

    // ✅ Role icons
    const getRoleIcon = (role) => {
        switch (role) {
            case 'SuperAdmin': return <CrownOutlined />;
            case 'Admin': return <SafetyOutlined />;
            case 'Viewer': return <EyeOutlined />;
            case 'Supplier': return <UserOutlined />;
            default: return <UserOutlined />;
        }
    };

    // ✅ Statistics
    const stats = {
        total: allSuppliers.length,
        superAdmin: allSuppliers.filter(s => s.role === 'SuperAdmin').length,
        admin: allSuppliers.filter(s => s.role === 'Admin').length,
        viewer: allSuppliers.filter(s => s.role === 'Viewer').length,
        supplier: allSuppliers.filter(s => s.role === 'Supplier').length,
    };

    // ✅ Table columns
    const columns = [
        {
            title: t('user'),
            key: 'user',
            width: 250,
            fixed: 'left',
            render: (_, record) => (
                <Space>
                    <Avatar
                        style={{
                            backgroundColor: getRoleColor(record.role),
                            verticalAlign: 'middle',
                        }}
                        size="large"
                        icon={getRoleIcon(record.role)}
                    />
                    <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#262626' }}>
                            {record.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            {record.email}
                        </div>
                    </div>
                </Space>
            ),
        },
        {
            title: t('supplier_code'),
            dataIndex: 'code',
            key: 'code',
            width: 150,
            render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: t('category'),
            dataIndex: 'category',
            key: 'category',
            width: 150,
        },
        {
            title: t('location'),
            dataIndex: 'location',
            key: 'location',
            width: 150,
        },
        {
            title: t('current_role'),
            dataIndex: 'role',
            key: 'role',
            width: 150,
            align: 'center',
            render: (role) => (
                <Tag icon={getRoleIcon(role)} color={getRoleColor(role)} style={{ fontSize: '13px' }}>
                    {role}
                </Tag>
            ),
        },
        {
            title: t('change_role'),
            key: 'action',
            width: 200,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                canChangeRole ? (
                    <Select
                        value={record.role}
                        style={{ width: 160 }}
                        onChange={newRole => handleRoleChange(record.supplierId, newRole, record.name)}
                        size="middle"
                    >
                        <Option value="Supplier">
                            <Space>
                                <UserOutlined style={{ color: '#fa8c16' }} />
                                {t('supplier')}
                            </Space>
                        </Option>
                        <Option value="Viewer">
                            <Space>
                                <EyeOutlined style={{ color: '#52c41a' }} />
                                {t('viewer')}
                            </Space>
                        </Option>
                        <Option value="Admin">
                            <Space>
                                <SafetyOutlined style={{ color: '#1890ff' }} />
                                {t('admin')}
                            </Space>
                        </Option>
                        <Option value="SuperAdmin">
                            <Space>
                                <CrownOutlined style={{ color: '#f5222d' }} />
                                {t('superadmin')}
                            </Space>
                        </Option>
                    </Select>
                ) : (
                    <Tooltip title={t('only_superadmin_can_change_roles')}>
                        <Tag color="default">{t('no_permission')}</Tag>
                    </Tooltip>
                )
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{
                background: '#fff',
                padding: '32px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #1890ff'
            }}>
                <h1 style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#262626',
                    margin: 0
                }}>
                    <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                    {t('role_management')}
                </h1>
                <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
                    {t('manage_user_roles_and_permissions')}
                </p>
            </div>

            {/* Permission Warning */}
            {!canChangeRole && (
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        borderLeft: '4px solid #faad14',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                >
                    <Space>
                        <WarningOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                        <div>
                            <div style={{ fontWeight: '600', fontSize: '16px', color: '#262626' }}>
                                {t('limited_access')}
                            </div>
                            <div style={{ fontSize: '14px', color: '#8c8c8c', marginTop: '4px' }}>
                                {t('only_superadmin_can_change_roles')}
                            </div>
                        </div>
                    </Space>
                </Card>
            )}

            {/* Statistics Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('total_users')}
                            value={stats.total}
                            prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Badge count={stats.superAdmin} offset={[10, 0]}>
                            <Statistic
                                title={t('super_admins')}
                                value={stats.superAdmin}
                                prefix={<CrownOutlined style={{ color: '#f5222d' }} />}
                                valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#f5222d' }}
                            />
                        </Badge>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('admins')}
                            value={stats.admin}
                            prefix={<SafetyOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Statistic
                            title={t('viewers')}
                            value={stats.viewer}
                            prefix={<EyeOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ fontSize: '32px', fontWeight: '700', color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Current User Info */}
            <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Space>
                    <CheckCircleOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                    <div>
                        <span style={{ fontSize: '14px', color: '#8c8c8c' }}>{t('logged_in_as')}: </span>
                        <strong style={{ fontSize: '16px', color: '#262626' }}>
                            {currentUser?.name || currentUser?.email}
                        </strong>
                        <Tag color={getRoleColor(currentUser?.role)} style={{ marginLeft: '12px' }}>
                            {currentUser?.role}
                        </Tag>
                    </div>
                </Space>
            </Card>

            {/* Filters */}
            <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={24} md={12}>
                        <Search
                            placeholder={t('search_by_name_email_or_code')}
                            allowClear
                            onSearch={(value) => setSearchText(value)}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="large"
                            prefix={<SearchOutlined />}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder={t('filter_by_role')}
                            allowClear
                            onChange={(value) => setFilterRole(value || 'all')}
                            style={{ width: '100%' }}
                            value={filterRole}
                            size="large"
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">{t('all_roles')}</Option>
                            <Option value="SuperAdmin">
                                <Space>
                                    <CrownOutlined style={{ color: '#f5222d' }} />
                                    {t('superadmin')}
                                </Space>
                            </Option>
                            <Option value="Admin">
                                <Space>
                                    <SafetyOutlined style={{ color: '#1890ff' }} />
                                    {t('admin')}
                                </Space>
                            </Option>
                            <Option value="Viewer">
                                <Space>
                                    <EyeOutlined style={{ color: '#52c41a' }} />
                                    {t('viewer')}
                                </Space>
                            </Option>
                            <Option value="Supplier">
                                <Space>
                                    <UserOutlined style={{ color: '#fa8c16' }} />
                                    {t('supplier')}
                                </Space>
                            </Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6} style={{ textAlign: 'right' }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchSuppliers}
                            loading={loading}
                            size="large"
                            style={{ width: '100%' }}
                        >
                            {t('refresh')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size="large" tip={t('loading')} />
                    </div>
                ) : filteredSuppliers.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('no_users_found')}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredSuppliers}
                        rowKey="supplierId"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `${t('total')} ${total} ${t('users')}`,
                        }}
                        scroll={{ x: 1200 }}
                        size="middle"
                    />
                )}
            </Card>
        </div>
    );
};

export default RoleAssignment;
